import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { ConsentCategory, ConsentDecision, ConsentMethod, ConsentState } from './types';
import {
  CONSENT_SCHEMA_VERSION,
  DEFAULT_CATEGORIES,
  readConsent,
  writeConsent,
} from './consent-storage';
import { applyConsent } from './consent-activator';

interface ConsentContextValue {
  state: ConsentState | null;
  categories: Record<ConsentCategory, ConsentDecision>;
  hasDecision: boolean;
  isModalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
  acceptAll: () => void;
  rejectAll: () => void;
  saveCustom: (categories: Record<ConsentCategory, ConsentDecision>) => void;
}

const ConsentContext = createContext<ConsentContextValue | null>(null);

function buildState(
  categories: Record<ConsentCategory, ConsentDecision>,
  method: ConsentMethod,
  locale: string
): ConsentState {
  return {
    version: CONSENT_SCHEMA_VERSION,
    timestamp: Date.now(),
    categories: { ...categories, necessary: 'granted' },
    locale,
    method,
  };
}

function hasDowngrade(
  prev: Record<ConsentCategory, ConsentDecision> | null,
  next: Record<ConsentCategory, ConsentDecision>
): boolean {
  if (!prev) return false;
  return (Object.keys(next) as ConsentCategory[]).some(
    (key) => prev[key] === 'granted' && next[key] === 'denied'
  );
}

export function ConsentProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const [state, setState] = useState<ConsentState | null>(() => readConsent());
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (state) applyConsent(state);
  }, [state]);

  const persist = useCallback(
    (next: Record<ConsentCategory, ConsentDecision>, method: ConsentMethod) => {
      const prev = state?.categories ?? null;
      const built = buildState(next, method, i18n.resolvedLanguage ?? 'en');
      writeConsent(built);
      setState(built);
      setIsModalOpen(false);
      if (hasDowngrade(prev, built.categories)) {
        window.location.reload();
      }
    },
    [state, i18n]
  );

  const acceptAll = useCallback(() => {
    persist(
      { necessary: 'granted', functional: 'granted', analytics: 'granted', marketing: 'granted' },
      'accept-all'
    );
  }, [persist]);

  const rejectAll = useCallback(() => {
    persist(
      { necessary: 'granted', functional: 'denied', analytics: 'denied', marketing: 'denied' },
      'reject-all'
    );
  }, [persist]);

  const saveCustom = useCallback(
    (categories: Record<ConsentCategory, ConsentDecision>) => {
      persist(categories, 'customize');
    },
    [persist]
  );

  const openModal = useCallback(() => setIsModalOpen(true), []);
  const closeModal = useCallback(() => setIsModalOpen(false), []);

  const value = useMemo<ConsentContextValue>(
    () => ({
      state,
      categories: state?.categories ?? DEFAULT_CATEGORIES,
      hasDecision: state !== null,
      isModalOpen,
      openModal,
      closeModal,
      acceptAll,
      rejectAll,
      saveCustom,
    }),
    [state, isModalOpen, openModal, closeModal, acceptAll, rejectAll, saveCustom]
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent(): ConsentContextValue {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error('useConsent must be used inside <ConsentProvider>');
  return ctx;
}
